'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { usePathname, useRouter, useSearchParams } from 'next/navigation';

import { SPEC_OPTIONS } from '@/features/search';

import type { Speciality } from '../types';
import { useDebounceSearch } from './useDebounceSearch';

export const SPECIALITY_CATEGORY_MAP: Record<string, string> = {
  'emergency-clinic-london': 'Primary Care',
  'aesthetics-clinic': 'Aesthetics',
  'audiology-hearing-test-battersea-london': 'ENT & Audiology',
  'blood-test': 'Tests & Scans',
  'bone-scan': 'Tests & Scans',
  'private-breast-clinic': "Women's Health",
  cardiology: 'Specialist Medicine',
  'dental-services': 'Dental',
  dermatology: 'Specialist Medicine',
  dietetics: 'Primary Care',
  ent: 'ENT & Audiology',
  exroid: 'Surgical',
  fertility: "Women's Health",
  'gastric-alimetry': 'Specialist Medicine',
  gastroenterology: 'Specialist Medicine',
  'general-surgery': 'Surgical',
  geriatrics: 'Primary Care',
  'private-gp': 'Primary Care',
  'gynaecology-and-obstetrics': "Women's Health",
  haematology: 'Specialist Medicine',
  'hip-replacement-and-knee-replacement': 'Surgical',
  arthrosamid: 'Surgical',
  'liver-health-liver-scans': 'Tests & Scans',
  'longevity-centre': 'Primary Care',
  maxillofacial: 'Surgical',
  'menopause-clinic': "Women's Health",
  'mole-mapping': 'Tests & Scans',
  neurology: 'Specialist Medicine',
  ophthalmology: 'Specialist Medicine',
  physiotherapy: 'Primary Care',
  'plastic-surgery': 'Surgical',
  psychiatry: 'Specialist Medicine',
  radiology: 'Tests & Scans',
  rheumatology: 'Specialist Medicine',
  'sexual-health': 'Primary Care',
  'lumps-and-bumps': 'Surgical',
  neurosurgery: 'Surgical',
  'trauma-and-orthopaedics-lower-limb': 'Surgical',
  'trauma-and-orthopaedics-upper-limb': 'Surgical',
  trt: "Men's Health",
  ultrasound: 'Tests & Scans',
  'urology-and-andrology': "Men's Health",
};

export const CATEGORY_OPTIONS = SPEC_OPTIONS.filter(
  (option) => (option.slug as string) !== 'General',
);

const PAGE_SIZE = 9;

interface UseSpecialitiesFilterProps {
  initialSpecialities: Speciality[];
  total: number;
}

export function useSpecialitiesFilter({
  initialSpecialities,
  total: initialTotal,
}: UseSpecialitiesFilterProps) {
  const searchParams = useSearchParams();
  const router = useRouter();
  const pathname = usePathname();
  const isFirstRender = useRef(true);
  const isFirstRenderSearch = useRef(true);

  const urlCategory = searchParams.get('category') || '';
  const urlSubCategory = searchParams.get('subCategory') || '';

  const [total, setTotal] = useState(initialTotal);
  const [loadedSpecialities, setLoadedSpecialities] =
    useState<Speciality[]>(initialSpecialities);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const { query, activeQuery, setQuery, applySearch } = useDebounceSearch();

  const [selectedCategory, setSelectedCategory] = useState(urlCategory);
  const [selectedSubSlugs, setSelectedSubSlugs] = useState<string[]>(() =>
    urlSubCategory ? urlSubCategory.split(',').filter(Boolean) : [],
  );
  const [openMenu, setOpenMenu] = useState<string | null>(null);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    if (activeQuery.trim()) return;
    setLoadedSpecialities(initialSpecialities);
    setTotal(initialTotal);
  }, [initialSpecialities, initialTotal, activeQuery]);

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 639px)');
    const update = () => setIsMobile(mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    const params = new URLSearchParams(searchParams.toString());

    if (selectedCategory) {
      params.set('category', selectedCategory);
      params.delete('q');
    } else {
      params.delete('category');
    }

    if (selectedSubSlugs.length > 0) {
      params.set('subCategory', selectedSubSlugs.join(','));
    } else {
      params.delete('subCategory');
    }

    router.replace(`${pathname}?${params.toString()}`, { scroll: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategory, selectedSubSlugs]);

  const subSpecialityOptions = useMemo<
    { slug: string; title: string }[]
  >(() => {
    if (!selectedCategory) return [];
    return [];
  }, [selectedCategory]);

  const subSpecialityTitleMap = useMemo(
    () =>
      Object.fromEntries(subSpecialityOptions.map((o) => [o.slug, o.title])),
    [subSpecialityOptions],
  );

  const subSpecsEnabled =
    selectedCategory !== '' && subSpecialityOptions.length > 0;

  useEffect(() => {
    const validSlugs = new Set(subSpecialityOptions.map((s) => s.slug));
    setSelectedSubSlugs((prev) => {
      const next = prev.filter((slug) => validSlugs.has(slug));
      return next.length === prev.length ? prev : next;
    });
  }, [subSpecialityOptions]);

  useEffect(() => {
    if (isFirstRenderSearch.current) {
      isFirstRenderSearch.current = false;
      return;
    }

    const queryParams = new URLSearchParams();
    queryParams.set('limit', PAGE_SIZE.toString());
    queryParams.set('offset', '0');

    const activeQ = activeQuery || '';
    const activeCategory = selectedCategory || '';
    const activeFilter = searchParams.get('filter') || '';

    if (activeQ) queryParams.set('q', activeQ);
    if (activeCategory) queryParams.set('category', activeCategory);
    if (activeFilter) queryParams.set('filter', activeFilter);

    setIsLoadingMore(true);
    fetch(`/api/specialities?${queryParams.toString()}`)
      .then((res) => res.json())
      .then((data) => {
        if (typeof data?.total === 'number') {
          setTotal(data.total);
        }
        if (data?.items) {
          setLoadedSpecialities(data.items);
        }
      })
      .catch((err) => console.error('Error fetching specialities:', err))
      .finally(() => setIsLoadingMore(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeQuery, selectedCategory]);

  const filtered = useMemo(() => {
    return loadedSpecialities;
  }, [loadedSpecialities]);

  const visible = useMemo(() => loadedSpecialities, [loadedSpecialities]);

  const hasMore = loadedSpecialities.length < total;
  const isFiltered = Boolean(
    activeQuery.trim() || selectedCategory || selectedSubSlugs.length > 0,
  );

  const handleLoadMore = useCallback(() => {
    if (isLoadingMore || loadedSpecialities.length >= total) return;
    setIsLoadingMore(true);

    const queryParams = new URLSearchParams();
    queryParams.set('limit', PAGE_SIZE.toString());
    queryParams.set('offset', loadedSpecialities.length.toString());

    const activeQ = activeQuery || '';
    const activeCategory = selectedCategory || '';
    const activeFilter = searchParams.get('filter') || '';

    if (activeQ) queryParams.set('q', activeQ);
    if (activeCategory) queryParams.set('category', activeCategory);
    if (activeFilter) queryParams.set('filter', activeFilter);

    fetch(`/api/specialities?${queryParams.toString()}`)
      .then((res) => res.json())
      .then((data) => {
        if (typeof data?.total === 'number') {
          setTotal(data.total);
        }
        if (data?.items) {
          setLoadedSpecialities((prev) => [...prev, ...data.items]);
        }
      })
      .catch((err) => console.error('Error fetching more specialities:', err))
      .finally(() => setIsLoadingMore(false));
  }, [
    loadedSpecialities.length,
    total,
    activeQuery,
    selectedCategory,
    searchParams,
    isLoadingMore,
  ]);

  const setVisibleCount = useCallback(() => {
    handleLoadMore();
  }, [handleLoadMore]);

  const handleQueryChange = useCallback(
    (value: string) => {
      setQuery(value);
      setOpenMenu(null);
      if (value.trim()) {
        setSelectedCategory('');
        setSelectedSubSlugs([]);
      }
    },
    [setQuery],
  );

  const handleSearch = useCallback(() => {
    if (query.trim()) {
      setSelectedCategory('');
      setSelectedSubSlugs([]);
    }
    applySearch();
    setOpenMenu(null);
  }, [applySearch, query]);

  const handleClearAll = useCallback(() => {
    applySearch('');
    setSelectedCategory('');
    setSelectedSubSlugs([]);
    setOpenMenu(null);
    const params = new URLSearchParams(searchParams.toString());
    params.delete('q');
    params.delete('category');
    params.delete('subCategory');
    params.delete('filter');
    router.replace(`${pathname}?${params.toString()}`, { scroll: false });
  }, [applySearch, pathname, router, searchParams]);

  const handleSelectCategory = useCallback(
    (cat: string) => {
      setSelectedCategory(cat);
      setSelectedSubSlugs([]);
      if (cat) {
        applySearch('');
      }
    },
    [applySearch],
  );

  const handleToggleSubSlug = useCallback((slug: string) => {
    setSelectedSubSlugs((prev) =>
      prev.includes(slug) ? prev.filter((s) => s !== slug) : [...prev, slug],
    );
  }, []);

  const handleClearSubSlugs = useCallback(() => {
    setSelectedSubSlugs([]);
  }, []);

  return {
    query,
    setQuery: handleQueryChange,
    openMenu,
    setOpenMenu,
    isMobile,
    selectedCategory,
    selectedSubSlugs,
    subSpecialityOptions,
    subSpecialityTitleMap,
    subSpecsEnabled,
    filtered,
    visible,
    hasMore,
    isFiltered,
    setVisibleCount,
    handleSearch,
    handleClearAll,
    handleSelectCategory,
    handleToggleSubSlug,
    handleClearSubSlugs,
  };
}
